import { t } from "../i18n/index.ts";
import {
  buildAgentMainSessionKey,
  parseAgentSessionKey,
  resolveUiConfiguredMainKey,
} from "../lib/sessions/session-key.ts";
import { showToast } from "../lib/toast.ts";
import { AppSidebarSessionNavigationElement } from "./app-sidebar-session-navigation.ts";
import type {
  SidebarRecentSession,
  SidebarSessionMutationResult,
  SidebarSessionMutationScope,
  SidebarSessionPatch,
} from "./app-sidebar-session-types.ts";
import type { SessionMenuAction } from "./session-menu.ts";

/** Session patch, fork, archive, and delete operations. */
export abstract class AppSidebarSessionMutationsElement extends AppSidebarSessionNavigationElement {
  protected readonly patchSession = async (
    session: SidebarRecentSession,
    patch: SidebarSessionPatch,
    scope: SidebarSessionMutationScope | null = this.beginSessionMutation(),
  ): Promise<SidebarSessionMutationResult> => {
    if (!scope) {
      return "stale";
    }
    const agentId = parseAgentSessionKey(session.key)?.agentId ?? scope.selectedAgentId;
    try {
      const patched = await scope.sessions.patch(session.key, patch, { agentId });
      if (!this.isSessionMutationScopeCurrent(scope)) {
        return "stale";
      }
      if (!patched) {
        if (scope.sessions.state.error) {
          this.publishSessionMutationError(scope, scope.sessions.state.error);
        }
        return "failed";
      }
      // Unpin from any surface (menu, pin button, drag) retires the session's
      // persisted zone slot; leaving it would resurrect stale synced entries.
      // Archiving implicitly unpins server-side (sessions-patch clears
      // pinnedAt), so it retires the slot too.
      if (patch.pinned === false || (patch.archived === true && session.pinned)) {
        this.pruneSidebarSessionEntry(session.key);
      }
      if (patch.archived !== true || !session.active) {
        return "completed";
      }
      this.replaceCurrentSession(
        buildAgentMainSessionKey({
          agentId,
          mainKey: resolveUiConfiguredMainKey({
            agentsList: scope.context.agents.state.agentsList,
            hello: scope.gateway.snapshot.hello,
          }),
        }),
      );
      return "completed";
    } catch (error) {
      if (!this.isSessionMutationScopeCurrent(scope)) {
        return "stale";
      }
      this.publishSessionMutationError(scope, error);
      return "failed";
    }
  };

  protected async patchSessions(
    rows: readonly SidebarRecentSession[],
    patch: SidebarSessionPatch,
    scope: SidebarSessionMutationScope | null = this.beginSessionMutation(),
  ): Promise<SidebarSessionMutationResult> {
    if (!scope) {
      return "stale";
    }
    let result: SidebarSessionMutationResult = "completed";
    // Sequential like deleteMany: parallel patches would race the shared
    // session-state publishes inside the capability.
    for (const row of rows) {
      const rowResult = await this.patchSession(row, patch, scope);
      if (rowResult === "stale") {
        return "stale";
      }
      if (rowResult === "failed") {
        result = "failed";
      }
    }
    return result;
  }

  protected async archiveSessionWithUndo(session: SidebarRecentSession) {
    const scope = this.beginSessionMutation();
    if (!scope) {
      return;
    }
    const result = await this.patchSession(session, { archived: true }, scope);
    if (result !== "completed" || !this.isSessionMutationScopeCurrent(scope)) {
      return;
    }
    showToast({
      message: t("sessionsView.sessionArchived"),
      actionLabel: t("common.undo"),
      onAction: () => {
        void this.restoreArchivedSessions([{ session, pinned: session.pinned }], scope);
      },
    });
  }

  private async archiveSessionsWithUndo(rows: readonly SidebarRecentSession[]) {
    const scope = this.beginSessionMutation();
    if (!scope) {
      return;
    }
    const archived: Array<{ session: SidebarRecentSession; pinned: boolean }> = [];
    for (const session of rows) {
      const result = await this.patchSession(session, { archived: true }, scope);
      if (result === "stale") {
        return;
      }
      if (result === "completed") {
        archived.push({ session, pinned: session.pinned });
      }
    }
    if (archived.length === 0 || !this.isSessionMutationScopeCurrent(scope)) {
      return;
    }
    showToast({
      message:
        archived.length === 1
          ? t("sessionsView.sessionArchived")
          : t("sessionsView.sessionsArchived", { count: String(archived.length) }),
      actionLabel: t("common.undo"),
      onAction: () => void this.restoreArchivedSessions(archived, scope),
    });
  }

  private async restoreArchivedSessions(
    archived: readonly { session: SidebarRecentSession; pinned: boolean }[],
    scope: SidebarSessionMutationScope,
  ) {
    if (!this.isSessionMutationScopeCurrent(scope)) {
      return;
    }
    let restoredActiveKey: string | null = null;
    for (const { session, pinned } of archived) {
      const result = await this.patchSession(
        session,
        { archived: false, ...(pinned ? { pinned: true } : {}) },
        scope,
      );
      if (result === "stale") {
        return;
      }
      if (result === "completed" && session.active) {
        restoredActiveKey = session.key;
      }
    }
    if (restoredActiveKey && this.isSessionMutationScopeCurrent(scope)) {
      this.replaceCurrentSession(restoredActiveKey);
    }
  }

  /** One confirm and one preserved-worktrees alert for the whole selection. */
  protected async deleteSessionsBatch(rows: readonly SidebarRecentSession[]) {
    if (rows.length === 0) {
      return;
    }
    if (!window.confirm(t("sessionsView.deleteSessionsConfirm", { count: String(rows.length) }))) {
      return;
    }
    const scope = this.beginSessionMutation();
    if (!scope) {
      return;
    }
    try {
      const result = await scope.sessions.deleteMany(
        rows.map((row) => ({
          key: row.key,
          agentId: parseAgentSessionKey(row.key)?.agentId ?? scope.selectedAgentId,
          deleteTranscript: true,
        })),
      );
      if (!this.isSessionMutationScopeCurrent(scope)) {
        return;
      }
      if (result.preservedWorktrees.length > 0) {
        window.alert(
          t("sessionsView.deletePreservedWorktrees", {
            count: String(result.preservedWorktrees.length),
            branches: result.preservedWorktrees.map((worktree) => worktree.branch).join(", "),
          }),
        );
        if (!this.isSessionMutationScopeCurrent(scope)) {
          return;
        }
      }
      const deletedActive = rows.find((row) => row.active && result.deleted.includes(row.key));
      if (deletedActive) {
        this.replaceCurrentSession(
          buildAgentMainSessionKey({
            agentId: parseAgentSessionKey(deletedActive.key)?.agentId ?? scope.selectedAgentId,
            mainKey: resolveUiConfiguredMainKey({
              agentsList: scope.context.agents.state.agentsList,
              hello: scope.gateway.snapshot.hello,
            }),
          }),
        );
      }
      if (result.errors.length > 0) {
        this.publishSessionMutationError(scope, result.errors.join("; "));
      }
    } catch (error) {
      this.publishSessionMutationError(scope, error);
    }
  }

  protected runBatchSessionAction(
    action: SessionMenuAction,
    rows: SidebarRecentSession[],
    allUnread: boolean,
  ) {
    switch (action.kind) {
      case "toggle-unread":
        void this.patchSessions(rows, { unread: !allUnread });
        break;
      case "move-to-group":
        void this.patchSessions(
          rows.filter((row) => (row.category ?? null) !== action.category),
          { category: action.category },
        );
        break;
      case "new-group":
        this.createSessionGroup(rows);
        break;
      case "toggle-archived":
        void this.archiveSessionsWithUndo(rows);
        break;
      case "delete":
        void this.deleteSessionsBatch(rows);
        break;
      default:
        break;
    }
  }

  protected async forkSession(session: SidebarRecentSession) {
    const scope = this.beginSessionMutation();
    if (!scope) {
      return;
    }
    const agentId = parseAgentSessionKey(session.key)?.agentId ?? scope.selectedAgentId;
    try {
      const key = await scope.sessions.create({
        parentSessionKey: session.key,
        fork: true,
        agentId,
      });
      if (!this.isSessionMutationScopeCurrent(scope)) {
        return;
      }
      if (key) {
        this.selectSession(key);
      } else {
        this.publishSessionMutationError(
          scope,
          scope.sessions.state.error ?? t("newSession.createFailed"),
        );
      }
    } catch (error) {
      this.publishSessionMutationError(scope, error);
    }
  }

  protected async stopCloudWorker(session: SidebarRecentSession) {
    if (
      !session.cloudWorkerActive ||
      session.hasActiveRun ||
      !window.confirm(t("sessionsView.stopCloudWorkerConfirm", { session: session.label }))
    ) {
      return;
    }
    const scope = this.beginSessionMutation();
    if (!scope) {
      return;
    }
    const agentId = parseAgentSessionKey(session.key)?.agentId ?? scope.selectedAgentId;
    try {
      await scope.client.request(
        "sessions.reclaim",
        { key: session.key, agentId },
        { timeoutMs: 10 * 60_000 },
      );
      if (!this.isSessionMutationScopeCurrent(scope)) {
        return;
      }
      await scope.sessions.refreshReplacement(agentId);
    } catch (error) {
      this.publishSessionMutationError(scope, error);
    }
  }

  protected async deleteSession(session: SidebarRecentSession) {
    if (!window.confirm(t("sessionsView.deleteSessionConfirm", { session: session.label }))) {
      return;
    }
    const scope = this.beginSessionMutation();
    if (!scope) {
      return;
    }
    const agentId = parseAgentSessionKey(session.key)?.agentId ?? scope.selectedAgentId;
    try {
      const outcome = await scope.sessions.delete(session.key, {
        agentId,
        deleteTranscript: true,
      });
      if (!this.isSessionMutationScopeCurrent(scope)) {
        return;
      }
      // Dirty/unpushed checkouts survive deletion; offer explicit removal.
      if (outcome.worktreePreserved) {
        const preserved = outcome.worktreePreserved;
        if (
          window.confirm(
            t("sessionsView.deletePreservedWorktreeConfirm", { branch: preserved.branch }),
          )
        ) {
          if (!this.isSessionMutationScopeCurrent(scope)) {
            return;
          }
          try {
            await scope.client.request("worktrees.remove", {
              id: preserved.id,
              force: true,
            });
          } catch (error) {
            this.publishSessionMutationError(scope, error);
          }
          if (!this.isSessionMutationScopeCurrent(scope)) {
            return;
          }
        }
      }
      if (!outcome.deleted || !session.active) {
        return;
      }
      this.replaceCurrentSession(
        buildAgentMainSessionKey({
          agentId,
          mainKey: resolveUiConfiguredMainKey({
            agentsList: scope.context.agents.state.agentsList,
            hello: scope.gateway.snapshot.hello,
          }),
        }),
      );
    } catch (error) {
      this.publishSessionMutationError(scope, error);
    }
  }

  protected abstract createSessionGroup(sessions?: readonly SidebarRecentSession[]): void;
}
